import * as THREE from "three";
import GeometryCommand from "./GeometryCommand";
import CommandIdEnum from "../enums/CommandIdEnums";
import CanvasInputListenerModeEnum from "../enums/CanvasInputListenerModeEnum";
import ICanvasInputListener from "../logic/ICanvasInputListener";
import RaycastIntersection from "../logic/RaycastIntersection";
import Document from "../model/document/Document";
import CommandStack from "../logic/CommandStack";
import Placement3d from "../model/Placement3d";
import ExtrudedLoopAnnotationData from "../model/sceneObjectData/ExtrudedLoopAnnotationData";
import ISceneObject from "../model/sceneObject/ISceneObject";
import createSceneObject from "../model/sceneObject/SceneObjectFactory";
import Constants from "../constants";
import { sceneUnitsFromMm } from "../utils/scaler";
import { useCurrentCommandState } from "../store/currentCommandStateStore";
import { buildPolygonRefAxes } from "./commandHelpers/polygonRefAxes";
import EditHandleTypeEnum from "../enums/EditHandleTypeEnum";
import { dragMeasurementText } from "../utils/dragMeasurement";

type PointOutcome = 'continue' | 'showFinish' | 'autoFinish';

// Shared creation flow for any annotation extruded from a closed loop, at a fixed extrusion
// height: lock a placement/plane on the first click, then collect further points on that plane
// until the shape decides it's done. Subclasses only need to say what the shape looks like after
// each click (createInitialData/onSubsequentPoint/buildPreviewData), what points to snap through
// (getRefAxisPoints), and when finishing is allowed (isReadyToFinish) — Polygon waits for an
// explicit Finish button over an arbitrary number of points, Rectangle/Circle always auto-finish
// after their fixed 2nd click.
export default abstract class AddExtrudedLoopAnnotationCmd<T extends ExtrudedLoopAnnotationData> extends GeometryCommand {

    protected static readonly FIXED_DEPTH = sceneUnitsFromMm(Constants.DEFAULT_EXTRUSION_DEPTH_MM);

    protected data: T | undefined;
    protected sceneObject: ISceneObject | null = null;
    protected nextPointPromptResourceKey: string = '';

    constructor(id: CommandIdEnum, commandStack: CommandStack, canvasInputListener: ICanvasInputListener, document: Document) {
        super(id, commandStack, canvasInputListener, document);
    }

    // ---- Template hooks — implemented per shape ----

    protected abstract createInitialData(placement: Placement3d, depth: number): T;
    protected abstract onSubsequentPoint(data: T, alignedPoint: THREE.Vector3): PointOutcome;
    // Returns a clone of data with a tentative point/corner/radius applied and applyAreaBasedFontSize() called, for live preview.
    protected abstract buildPreviewData(data: T, alignedPoint: THREE.Vector3): T;
    protected abstract getRefAxisPoints(data: T): THREE.Vector3[];
    protected abstract isReadyToFinish(data: T): boolean;
    protected abstract finishButtonResourceKey(): string;

    // Cursor-following hint shown before the first click (e.g. a small circle/corner marker).
    // No-op by default — only Rectangle/Circle override this; Polygon has no shape to hint at yet.
    protected updatePointHint(_point: THREE.Vector3, _normal: THREE.Vector3): void {}
    protected hidePointHint(): void {}

    // ---- Shared lifecycle ----

    start() {
        super.start();
        super.addToStack();
        this.raycastRecoil = sceneUnitsFromMm(10);
        this.activate();
    }

    activate() {
        this.connectMouse();
        this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_TARGET_OBJECTS);
        this.canvasInputListener.resetTargetObjects();
    }

    deactivate() {
        this.disconnectMouse();
    }

    getObject(): ISceneObject {
        if (!this.sceneObject) {
            if (!this.data) {
                throw new Error("Data for AddExtrudedLoopAnnotationCmd is not set");
            }
            this.sceneObject = createSceneObject(this.data);
            this.addToDrawables(this.sceneObject);
        }
        return this.sceneObject;
    }

    // Derives a stable in-plane reference direction for the given plane normal. On a horizontal
    // surface (floor/ceiling, normal ~ world Z) the fallback reference is world X rotated by the
    // model's own rotation offset — matching the convention used by InputCommand's refAxisHelper
    // and buildPolygonRefAxes — so a shape whose edges are defined directly in this local frame
    // (Rectangle) comes out aligned with the model instead of a fixed world axis. On a vertical
    // surface (wall) the normal itself already comes from the actual, correctly-rotated mesh, so
    // no offset is needed there.
    deriveRefDirection(normal: THREE.Vector3): THREE.Vector3 {
        const worldUp = new THREE.Vector3(0, 0, 1);
        const isHorizontal = Math.abs(normal.dot(worldUp)) > Constants.HORIZONTAL_SURFACE_DOT_THRESHOLD;
        const reference = isHorizontal
            ? new THREE.Vector3(1, 0, 0).applyAxisAngle(worldUp, this.modelRotationOffset)
            : worldUp;
        return reference.clone().cross(normal).normalize();
    }

    point(raycastResult: RaycastIntersection): boolean {

        let autoFinish = false;

        if (!this.data) {
            // First point: establish the profile's placement and lock the plane
            const location = raycastResult.point.clone();
            this.addRecoil(location);
            super.setParent(raycastResult.object);

            const normal = this.planeGridHelper.activeNormal?.clone() || raycastResult.normalInWcs?.clone() || new THREE.Vector3(0, 0, 1);
            const refDirection = this.deriveRefDirection(normal);
            const placement = new Placement3d(location, refDirection, normal);

            this.data = this.createInitialData(placement, AddExtrudedLoopAnnotationCmd.FIXED_DEPTH);

            this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_PLANE);
            this.canvasInputListener.setTargetPlane(new THREE.Plane().setFromNormalAndCoplanarPoint(normal.clone(), location.clone()));

            // Plane is now locked: no more plane grid / switch-plane / point-hint affordance needed
            this.hidePlaneGrid();
            this.hideSwitchPlaneButton();
            this.hidePointHint();

            this.enableReferenceLine();
            this.refAxisHelper.setAxes(buildPolygonRefAxes(this.getRefAxisPoints(this.data), this.modelRotationOffset, false));
            this.startMeasure(location.clone());

            this.showInfoMessage(this.nextPointPromptResourceKey);
        }
        else {
            // Subsequent points: snap to reference axes, then let the shape decide what happens
            const alignedPoint = this.getRefLineAlignedPoint(raycastResult.point.clone());
            const outcome = this.onSubsequentPoint(this.data, alignedPoint);

            this.refAxisHelper.setAxes(buildPolygonRefAxes(this.getRefAxisPoints(this.data), this.modelRotationOffset, false));
            this.startMeasure(alignedPoint.clone());

            if (outcome === 'showFinish') {
                this.showFinishButton();
            } else if (outcome === 'autoFinish') {
                autoFinish = true;
            }
        }

        this.getObject().geometry.update(this.data, false);

        if (autoFinish) {
            this.onFinish();
        }

        return true;
    }

    pointerMove(raycastResult: RaycastIntersection): boolean {

        if (!this.data) {
            if (raycastResult && (raycastResult.normalInWcs || this.planeGridHelper.activeNormal)) {
                this.showPlaneGrid(raycastResult.point, raycastResult.normalInWcs);
                const normal = this.planeGridHelper.activeNormal?.clone() || raycastResult.normalInWcs!.clone();
                this.updatePointHint(raycastResult.point, normal);
            } else {
                this.hidePlaneGrid();
                this.hidePointHint();
            }
        }

        if (!raycastResult || !this.data) {
            return true;
        }

        super.pointerMove(raycastResult);

        const alignedPoint = this.getRefLineAlignedPoint(raycastResult.point.clone());
        this.updateRefLine(alignedPoint);

        const preview = this.buildPreviewData(this.data, alignedPoint);

        // Overwrites the point-to-point distance super.pointerMove() just set, for shapes whose
        // creation drag sets a more meaningful quantity than the distance travelled.
        const measurementHandleType = this.creationMeasurementHandleType();
        const measureText = measurementHandleType
            ? dragMeasurementText(preview.getDragMeasurement(measurementHandleType))
            : undefined;
        if (measureText) useCurrentCommandState.getState().setMeasureText(measureText);

        this.getObject().geometry.update(preview, false);

        return true;
    }

    // The quantity this creation drag sets, named as the equivalent edit-handle kind. Undefined
    // keeps the generic distance readout (a polygon's next point really is a distance).
    protected creationMeasurementHandleType(): EditHandleTypeEnum | undefined {
        return undefined;
    }

    showFinishButton() {
        if (!this.data) return;
        useCurrentCommandState.getState().showNextButton(() => this.onFinish(), this.finishButtonResourceKey());
    }

    onFinish() {
        if (!this.data || !this.isReadyToFinish(this.data)) {
            return;
        }

        useCurrentCommandState.getState().hideNextButton();

        // Redundant with the calls in point(), but guarantees the persisted shape's font size
        // matches its final area even if that ever changes.
        this.data.applyAreaBasedFontSize();

        if (!this.persistEntity(this.data)) {
            console.error("Failed to save annotation. Please try again.");
            alert("Failed to save annotation. Please try again.");
            return;
        }

        this.end();
    }

    end() {
        this.deactivate();
        this.stopMeasure();
        this.hidePointHint();
        useCurrentCommandState.getState().hideNextButton();
        super.end();
        this.removeFromStack();
        this.data = undefined;
        this.sceneObject = null;
    }

    // Plane switching is only available before the first point locks the profile plane
    onSwitchPlane(): void {
        if (this.data) return;
        super.onSwitchPlane();
    }

    rightClick(event: MouseEvent, raycaster: THREE.Raycaster, targets: THREE.Object3D[]) {
        this.onSwitchPlane();
    }

    // User can change viewer mode before the first point has been set.
    // After that, the command is terminated if viewer mode changes.
    onViewerModeChange(): void {
        if (!this.data) {
            return;
        }
        this.end();
    }
}
